import { useEffect, useState } from "react";
import axiosInstance from "../utils/AxiosInstance";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

const COLORS = ["#39e991", "#00cba9", "#aacfd0", "#cbeefc", "#ff7675", "#74b9ff", "#ffeaa7", "#fd79a8"];
const UNKNOWN_COLOR = "#888888";

const INTERVAL_OPTIONS = [
  { label: "7 Days", value: 7 },
  { label: "30 Days", value: 30 },
  { label: "90 Days", value: 90 },
  { label: "All Time", value: 9999 }
];

// Example years list, can be generated dynamically if you want
const YEAR_OPTIONS = [
  { label: "All Years", value: "" },
  { label: "2025", value: "2025" },
  { label: "2024", value: "2024" }
];

export default function RansomwareVictimCharts() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [includeUnknown, setIncludeUnknown] = useState(true);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  // AI summary state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      try {
        let url = `/insights/ransomware-victims/stats/?days=${days}`;
        if (selectedYear) {
          url += `&year=${selectedYear}`;
        }
        if (selectedGroup) {
          url += `&group_name=${encodeURIComponent(selectedGroup)}`;
        }
        const res = await axiosInstance.get(url);
        setStats(res.data);
        setAiSummary(null); // reset AI summary on new stats fetch
        setAiError(null);
      } catch (err) {
        console.error("Failed to load ransomware stats", err);
        setStats(null);
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [days, selectedYear, selectedGroup]);

  const toggleUnknown = () => setIncludeUnknown(prev => !prev);

  const formatLabel = (label) => {
    if (!label || label.trim() === "" || label === "Not Found") return "Unknown";
    return label;
  };

  // Filter out unknown entries if toggle is off
  const filterUnknown = (data, key) =>
    includeUnknown ? data : data.filter(item => item[key] && item[key] !== "Unknown");

  // Fetch AI summary from backend
  const fetchAISummary = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiSummary(null);

    try {
      const res = await axiosInstance.post("/insights/ransomware-victims/ai-summary/", {
        days,
        year: selectedYear,
        group_name: selectedGroup,
        interval: "daily",
      });

      setAiSummary(res.data.summary);
    } catch (err) {
      console.error("Failed to fetch AI summary", err);
      setAiError(err.response?.data?.error || "Failed to generate summary");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return <p className="text-gray-500 dark:text-gray-400">Loading ransomware victim stats...</p>;
  }

  if (!stats) {
    return <p className="text-red-500 dark:text-red-400">No stats available.</p>;
  }

  return (
    <section className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800 rounded-xl p-6 mb-10">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className="text-xl font-bold text-purple-700 dark:text-purple-300">📊 Ransomware Statistics</h2>
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex gap-2">
            {INTERVAL_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setDays(value)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                  days === value
                    ? "bg-purple-600 text-white border-purple-500"
                    : "bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-300 border-gray-400 dark:border-gray-700 hover:border-purple-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Group filter dropdown */}
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-400 dark:border-gray-700 rounded-md px-3 py-1 text-sm"
            aria-label="Filter stats by group"
          >
            <option value="">All Groups</option>
            {stats.by_group.map(group => (
              <option key={group.group} value={group.group}>
                {group.group || "Unknown"}
              </option>
            ))}
          </select>

          {/* Year filter dropdown */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-400 dark:border-gray-700 rounded-md px-3 py-1 text-sm"
            aria-label="Filter stats by year"
          >
            {YEAR_OPTIONS.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeUnknown}
              onChange={toggleUnknown}
              className="accent-purple-500"
            />
            Include Unknown
          </label>
        </div>
      </div>

      {/* Charts */}
      <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3">
        {/* Victims by Group */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-300 dark:border-gray-700">
          <h3 className="text-purple-700 dark:text-purple-300 font-semibold mb-2">By Group</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={filterUnknown(
                stats.by_group.map(g => ({
                  ...g,
                  group: formatLabel(g.group)
                })),
                "group"
              )}
              margin={{ top: 10, right: 10, bottom: 40, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="group" tick={{ fill: "#444", fontSize: 12 }} angle={-30} textAnchor="end" interval={0} />
              <YAxis tick={{ fill: "#444", fontSize: 12 }} />
              <Tooltip />
              <Legend verticalAlign="top" height={36} />
              <Bar dataKey="count" fill="#ff7675" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Victims by Sector */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-300 dark:border-gray-700">
          <h3 className="text-purple-700 dark:text-purple-300 font-semibold mb-2">By Sector</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={filterUnknown(
                stats.by_sector.map(s => ({
                  ...s,
                  sector: formatLabel(s.sector)
                })),
                "sector"
              )}
              margin={{ top: 10, right: 10, bottom: 40, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="sector" tick={{ fill: "#444", fontSize: 12 }} angle={-30} textAnchor="end" interval={0} />
              <YAxis tick={{ fill: "#444", fontSize: 12 }} />
              <Tooltip />
              <Legend verticalAlign="top" height={36} />
              <Bar dataKey="count" fill="#00cba9" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Victims by Country */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-300 dark:border-gray-700">
          <h3 className="text-purple-700 dark:text-purple-300 font-semibold mb-2">By Country</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={filterUnknown(
                  stats.by_country.map(c => ({
                    ...c,
                    country: formatLabel(c.country)
                  })),
                  "country"
                )}
                dataKey="count"
                nameKey="country"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ country, count }) => `${formatLabel(country)} (${count})`}
              >
                {stats.by_country.map((item, i) => (
                  <Cell
                    key={i}
                    fill={item.country ? COLORS[i % COLORS.length] : UNKNOWN_COLOR}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} victims`} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Victims Over Time - Line Chart */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-300 dark:border-gray-700 sm:col-span-2 xl:col-span-3">
          <h3 className="text-purple-700 dark:text-purple-300 font-semibold mb-2">Victims Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={stats.time_series.map(item => ({
                ...item,
                date: new Date(item.date_occurred || item.bucket).toLocaleDateString()
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="date" tick={{ fill: "#444", fontSize: 12 }} />
              <YAxis tick={{ fill: "#444", fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#39e991" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Summary Section */}
      <div className="mt-8 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-300 dark:border-gray-700">
        <button
          onClick={fetchAISummary}
          disabled={aiLoading}
          className={`px-4 py-2 font-semibold rounded-md ${
            aiLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-purple-600 hover:bg-purple-700 text-white"
          }`}
        >
          {aiLoading ? "Generating Summary..." : "Generate AI Summary"}
        </button>

        {aiError && (
          <p className="mt-3 text-red-500 dark:text-red-400">{aiError}</p>
        )}

        {aiSummary && (
          <div className="mt-4 whitespace-pre-line text-gray-800 dark:text-gray-200">
            <h3 className="font-semibold mb-2 text-purple-700 dark:text-purple-300">
              AI Summary:
            </h3>
            <p>{aiSummary}</p>
          </div>
        )}
      </div>
    </section>
  );
}
